import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { environment } from "../../environments/environment";
import { VariantConfigData } from "../master/variant-config/variant-config.component";
import { TrackingData } from "../epack-lifecycle-tracking/epack-lifecycle-tracking.component";
import { Viconfig } from "../master/visual-inspection-config/visual-inspection-config.component";

export type BatteryType = "tg" | "piccolo" | "hv";

@Injectable({
  providedIn: "root",
})
export class EventService {
  EVENT_BASEURL = environment.eventApiUrl;
  constructor(private http: HttpClient) {}

  getEpackEolRawParams(payload) {
    return this.http
      .post(`${this.EVENT_BASEURL}/exponent/epack-eol/get-raw-params`, payload)
      .pipe(
        map((Response) => {
          return Response;
        }),
        catchError((errorRes) => {
          return throwError(errorRes);
        })
      );
  }

  getEpackEolRawData(payload) {
    return this.http
      .post(`${this.EVENT_BASEURL}/exponent/epack-eol/get-raw-data`, payload)
      .pipe(
        map((Response) => {
          return Response;
        }),
        catchError((errorRes) => {
          return throwError(errorRes);
        })
      );
  }

  getEpackEolTestDates(payload) {
    return this.http
      .post(`${this.EVENT_BASEURL}/exponent/epack-eol/get-test-dates`, payload)
      .pipe(
        map((Response) => {
          return Response;
        }),
        catchError((errorRes) => {
          return throwError(errorRes);
        })
      );
  }

  getEpackEolTestsByDate(payload) {
    return this.http
      .post(
        `${this.EVENT_BASEURL}/exponent/epack-eol/get-tests-by-date`,
        payload
      )
      .pipe(
        map((Response) => {
          return Response;
        }),
        catchError((errorRes) => {
          return throwError(errorRes);
        })
      );
  }

  getEpackEolData() {
    return this.http
      .get(`${this.EVENT_BASEURL}/exponent/epack-eol/get-epack-data`)
      .pipe(
        map((Response) => {
          return Response;
        }),
        catchError((errorRes) => {
          return throwError(errorRes);
        })
      );
  }

  getMetaDataByTestId(payload) {
    return this.http
      .post(
        `${this.EVENT_BASEURL}/exponent/epack-eol/get-metadata-by-testid`,
        payload
      )
      .pipe(
        map((Response) => {
          return Response;
        }),
        catchError((errorRes) => {
          return throwError(errorRes);
        })
      );
  }

  getCsvReportMetaData(battery_type: BatteryType) {
    return this.http
      .get(
        `${this.EVENT_BASEURL}/exponent/epack-eol/get-csv-report-metadata?battery_type=${battery_type}`
      )
      .pipe(
        map((Response) => {
          return Response;
        }),
        catchError((errorRes) => {
          return throwError(errorRes);
        })
      );
  }

  getYearlyTestsGraphData() {
    return this.http
      .get(`${this.EVENT_BASEURL}/exponent/epack-eol/get-yearly-tests-count`)
      .pipe(
        map((Response) => {
          return Response;
        }),
        catchError((errorRes) => {
          return throwError(errorRes);
        })
      );
  }

  getMonthlyTestsGraphData(year: number) {
    return this.http
      .get<{
        success: boolean;
        data: Record<
          BatteryType,
          {
            completed: Record<string, number[]>;
            pending: Record<string, number[]>;
          }
        >;
      }>(
        `${this.EVENT_BASEURL}/exponent/epack-eol/get-monthly-tests-count?year=${year}`
      )
      .pipe(
        map((Response) => {
          return Response;
        }),
        catchError((errorRes) => {
          return throwError(errorRes);
        })
      );
  }

  getEpackEolDataByDateRange(payload) {
    return this.http
      .post(
        this.EVENT_BASEURL + `/exponent/epack-eol/get-epack-data-by-date-range`,
        payload
      )
      .pipe(
        map((Response) => {
          return Response;
        }),
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  getAnalyticsConfigData(pack_type: BatteryType) {
    return this.http
      .get(
        this.EVENT_BASEURL +
          `/exponent/analytics/get-all-graph-config?battery_type=${pack_type}`
      )
      .pipe(
        map((Response) => {
          return Response;
        }),
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  getAnalyticsConfigDataById(payload: string) {
    return this.http
      .get(this.EVENT_BASEURL + `/exponent/analytics/get-graph-data/${payload}`)
      .pipe(
        (map((Response) => {
          return Response;
        }),
        catchError((error) => {
          return throwError(error);
        }))
      );
  }

  updateGraphDataByGraphId(payload) {
    return this.http
      .put(
        this.EVENT_BASEURL + `/exponent/analytics/update-graph-data`,
        payload
      )
      .pipe(
        (map((Response) => {
          return Response;
        }),
        catchError((error) => {
          return throwError(error);
        }))
      );
  }

  getChartsConfig() {
    return this.http
      .get(this.EVENT_BASEURL + "/exponent/analytics/get-chart-configs")
      .pipe(
        map((Res) => {
          return Res;
        }),
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  getSelectedAnalyticsSignals() {
    return this.http
      .get(this.EVENT_BASEURL + "/exponent/analytics/get-selected-signals")
      .pipe(
        map((Response) => {
          return Response;
        }),
        catchError((errorRes) => {
          return throwError(errorRes);
        })
      );
  }

  getRawDataBySignals(payload) {
    return this.http
      .post(
        this.EVENT_BASEURL +
          "/exponent/analytics/get-raw-data-by-selected-signals",
        payload
      )
      .pipe(
        map((Response) => {
          return Response;
        }),
        catchError((errorRes) => {
          return throwError(errorRes);
        })
      );
  }

  getSelectedSignalsByGraphId() {
    return this.http
      .get(
        this.EVENT_BASEURL +
          "/exponent/analytics/get-selected-signals-by-graphid"
      )
      .pipe(
        map((res) => {
          return res;
        }),
        catchError((err) => {
          return throwError(err);
        })
      );
  }

  getGroupedMetaDataByTestid(testId: string) {
    return this.http
      .get(
        `${this.EVENT_BASEURL}/exponent/epack-eol/get-metadata-by-group/${testId}`
      )
      .pipe(
        map((res) => {
          return res;
        }),
        catchError((err) => {
          return throwError(err);
        })
      );
  }

  getEpackEolDataByTestRig() {
    return this.http
      .get(
        `${this.EVENT_BASEURL}/exponent/epack-eol/get-epack-data-with-rig-data`
      )
      .pipe(
        map((Response) => {
          return Response;
        }),
        catchError((errorRes) => {
          return throwError(errorRes);
        })
      );
  }

  getPicoloEpackEolDataByTestRig() {
    return this.http
      .get(
        `${this.EVENT_BASEURL}/exponent/picolo/epack-eol/get-epack-data-with-rig-data`
      )
      .pipe(
        map((Response) => {
          return Response;
        }),
        catchError((errorRes) => {
          return throwError(errorRes);
        })
      );
  }

  getEpackTestsByDateRangeWithRigData(payload) {
    return this.http
      .post(
        `${this.EVENT_BASEURL}/exponent/epack-eol/get-epack-data-by-date-range-with-rig-data`,
        payload
      )
      .pipe(
        map((Response) => {
          return Response;
        }),
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  getRawDataBySignalsvsParentSignal(payload) {
    return this.http
      .post(
        this.EVENT_BASEURL +
          "/exponent/analytics/get-raw-data-by-selected-signals-vs-parent-signal",
        payload
      )
      .pipe(
        map((Response) => {
          return Response;
        }),
        catchError((errorRes) => {
          return throwError(errorRes);
        })
      );
  }

  getHistogramSignalsList() {
    interface DataByBatteryType {
      rigs: string[];
      products: string[];
      variants: Record<string, string[]>;
      signals: { signal: string; data_type: string; label: string }[];
    }

    return this.http
      .get<{
        success: boolean;
        data: {
          tg: DataByBatteryType;
          piccolo: DataByBatteryType;
        };
      }>(this.EVENT_BASEURL + `/exponent/analytics/histogram/get-signal-list`)
      .pipe(
        map((Response) => {
          return Response;
        }),
        catchError((errorRes) => {
          return throwError(errorRes);
        })
      );
  }

  getHistogramDataBySignal(payload: {
    signal: string;
    batteryType: BatteryType;
    product?: string;
    variant?: string;
    startDate?: string;
    endDate?: string;
  }) {
    return this.http
      .post<{
        success: boolean;
        data: {
          value: number;
          pack_id: string;
        }[];
      }>(
        this.EVENT_BASEURL + "/exponent/analytics/histogram/get-data-by-signal",
        payload
      )
      .pipe(
        map((Response) => {
          return Response;
        }),
        catchError((errorRes) => {
          return throwError(errorRes);
        })
      );
  }

  getProductVariantsList() {
    return this.http
      .get<{
        success: boolean;
        data: {
          product: string;
          variants: string[];
        }[];
      }>(this.EVENT_BASEURL + "/exponent/epack-eol/product-variant-config")
      .pipe(
        map((res) => res),
        catchError((err) => throwError(err))
      );
  }

  getProductVariantConfig(payload: { product: string; variant: string }) {
    return this.http
      .post<{
        success: boolean;
        data: VariantConfigData[];
      }>(
        this.EVENT_BASEURL + "/exponent/epack-eol/product-variant-config",
        payload
      )
      .pipe(
        map((res) => res),
        catchError((err) => throwError(err))
      );
  }

  updateProductVariantConfig(payload: {
    product: string;
    variant: string;
    data: {
      [key: string]: number;
    };
  }) {
    return this.http
      .put<{
        success: boolean;
        message: string;
      }>(
        this.EVENT_BASEURL + "/exponent/epack-eol/product-variant-config",
        payload
      )
      .pipe(
        map((res) => res),
        catchError((err) => throwError(err))
      );
  }

  getIpqcTableDashboardData(packId?: string) {
    let URL = this.EVENT_BASEURL + "/exponent/bms-ipqc/dashboard-table-data";

    if (packId) {
      URL = URL + `?pack_id=${packId}`;
    }

    return this.http.get(URL).pipe(
      map((res) => res),
      catchError((err) => throwError(err))
    );
  }

  getIpqcTestMetaData(test_id: string) {
    return this.http
      .post(this.EVENT_BASEURL + "/exponent/bms-ipqc/test-metadata", {
        test_id,
      })
      .pipe(
        map((res) => res),
        catchError((err) => throwError(err))
      );
  }

  getIpqcDashboardYearlyTestsData() {
    return this.http
      .get(
        this.EVENT_BASEURL + "/exponent/bms-ipqc/dashboard-yearly-graph-data"
      )
      .pipe(
        map((res) => res),
        catchError((err) => throwError(err))
      );
  }

  getIpqcDashboardMonthlyTestsData(year: number) {
    return this.http
      .get(
        this.EVENT_BASEURL +
          `/exponent/bms-ipqc/dashboard-monthly-graph-data/${year}`
      )
      .pipe(
        map((res) => res),
        catchError((err) => throwError(err))
      );
  }

  getEpackLifecycleTrackingData(packId: string) {
    const payload = { pack_id: packId };

    return this.http
      .post<{
        success: boolean;
        message: string;
        battery_type: "tg" | "piccolo";
        data: TrackingData;
      }>(this.EVENT_BASEURL + "/exponent/epack/lifecycle-tracking", payload, {
        observe: "response",
      })
      .pipe(
        map((res) => res),
        catchError((err) => throwError(err))
      );
  }

  createVisualInspection(payload: {
    product: string;
    variant: string;
    data: string[];
  }) {
    return this.http
      .post<{
        success: boolean;
        message: string;
        data: Viconfig[];
      }>(this.EVENT_BASEURL + "/exponent/pdi/vi-config", payload, {
        observe: "response",
      })
      .pipe(
        map((res) => res),
        catchError((err) => throwError(err))
      );
  }

  getConfigByProductVariant(payload: { product: string; variant: string }) {
    return this.http
      .post<{
        success: boolean;
        message: string;
        data: Array<Viconfig>;
      }>(
        this.EVENT_BASEURL + "/exponent/pdi/vi-config/get-by-product-variant",
        payload,
        {
          observe: "response",
        }
      )
      .pipe(
        map((res) => res),
        catchError((err) => throwError(err))
      );
  }

  getpdiDashboardYearlyTestsData() {
    return this.http
      .get<{
        success: boolean;
        message: string;
        data: {
          [year: string]: number[];
        };
      }>(this.EVENT_BASEURL + "/exponent/pdi/dashboard-yearly-graph-data", {
        observe: "response",
      })
      .pipe(
        map((res) => res),
        catchError((err) => throwError(err))
      );
  }

  getpdiDashboardMonthlyTestsData(year: number) {
    return this.http
      .get<{
        success: boolean;
        message: string;
        data: {
          passing: { [month: string]: number[] };
          failing: { [month: string]: number[] };
        };
      }>(
        this.EVENT_BASEURL +
          "/exponent/pdi/dashboard-monthly-graph-data/" +
          year,
        {
          observe: "response",
        }
      )
      .pipe(
        map((res) => res),
        catchError((err) => throwError(err))
      );
  }

  getPdiDashboardTableData() {
    return this.http
      .get<{
        success: boolean;
        message: string;
        data: {
          test_id: string;
          pack_id: string;
          bms_id: string;
          date: string;
          test_result: string;
        }[];
      }>(this.EVENT_BASEURL + "/exponent/pdi/dashboard-table-data", {
        observe: "response",
      })
      .pipe(
        map((res) => res),
        catchError((err) => throwError(err))
      );
  }

  getPdiDialougeData(testId: string) {
    return this.http
      .get<{
        success: boolean;
        message: string;
        data: { [key: string]: string };
      }>(this.EVENT_BASEURL + "/exponent/pdi/dialouge-data/" + testId, {
        observe: "response",
      })
      .pipe(
        map((res) => res),
        catchError((err) => throwError(err))
      );
  }
  downloadPDITestPdfReport(testId) {
    return this.http
      .post(`${this.EVENT_BASEURL}/exponent/pdi/pdi-test-pdf-download`, {
        testId,
      })
      .pipe(
        map((Response) => {
          return Response;
        }),
        catchError((errorRes) => {
          return throwError(errorRes);
        })
      );
  }

  getPdiLiteCsvReportData(battery_type: "piccolo" | "tg") {
    return this.http
      .get<{
        success: boolean;
        message: string;
        data: { [key: string]: string }[];
      }>(
        `${this.EVENT_BASEURL}/exponent/pdi-lite/csv-report-data?battery_type=${battery_type}`,
        {
          observe: "response",
        }
      )
      .pipe(
        map((res) => res),
        catchError((errorRes) => {
          return throwError(errorRes);
        })
      );
  }

  getPdiCsvReportData(battery_type: "piccolo" | "tg") {
    return this.http
      .get<{
        success: boolean;
        message: string;
        data: { [key: string]: string }[];
      }>(
        `${this.EVENT_BASEURL}/exponent/pdi/csv-report-data?battery_type=${battery_type}`,
        {
          observe: "response",
        }
      )
      .pipe(
        map((res) => res),
        catchError((errorRes) => {
          return throwError(errorRes);
        })
      );
  }

  getIpqcCsvReportData(battery_type: BatteryType) {
    return this.http
      .get<{
        success: boolean;
        message: string;
        data: { [key: string]: string }[];
      }>(
        `${this.EVENT_BASEURL}/exponent/bms-ipqc/csv-report-data?battery_type=${battery_type}`,
        {
          observe: "response",
        }
      )
      .pipe(
        map((res) => res),
        catchError((errorRes) => {
          return throwError(errorRes);
        })
      );
  }

  getPdiLiteDashboardTableData(battery_type: "tg" | "piccolo") {
    return this.http
      .get<{
        success: boolean;
        message: string;
        data: { [key: string]: string }[];
      }>(
        `${this.EVENT_BASEURL}/exponent/pdi-lite/dashboard-table-data?battery_type=${battery_type}`,
        {
          observe: "response",
        }
      )
      .pipe(
        map((res) => res),
        catchError((errorRes) => {
          return throwError(errorRes);
        })
      );
  }

  downloadPDILiteTestPdfReport(testId: string) {
    return this.http
      .post(`${this.EVENT_BASEURL}/exponent/pdi-lite/pdi-test-pdf-download`, {
        testId,
      })
      .pipe(
        map((Response) => {
          return Response;
        }),
        catchError((errorRes) => {
          return throwError(errorRes);
        })
      );
  }

  getPdiLiteDialougeData(testId: string) {
    return this.http
      .get<{
        success: boolean;
        message: string;
        battery_type: BatteryType;
        data: { [key: string]: string };
      }>(this.EVENT_BASEURL + "/exponent/pdi-lite/dialouge-data/" + testId, {
        observe: "response",
      })
      .pipe(
        map((res) => res),
        catchError((err) => throwError(err))
      );
  }

  getPicoloGroupedMetaDataByTestid(testId: string) {
    return this.http
      .get(
        `${this.EVENT_BASEURL}/exponent/picolo/epack-eol/get-metadata-by-group/${testId}`
      )
      .pipe(
        map((res) => {
          return res;
        }),
        catchError((err) => {
          return throwError(err);
        })
      );
  }

  getPicoloEpackTestsByDateRangeWithRigData(payload) {
    return this.http
      .post(
        `${this.EVENT_BASEURL}/exponent/picolo/epack-eol/get-epack-data-by-date-range-with-rig-data`,
        payload
      )
      .pipe(
        map((Response) => {
          return Response;
        }),
        catchError((error) => {
          return throwError(error);
        })
      );
  }

  getPicoloEpackEolRawData(payload) {
    return this.http
      .post(
        `${this.EVENT_BASEURL}/exponent/picolo/epack-eol/get-raw-data`,
        payload
      )
      .pipe(
        map((Response) => {
          return Response;
        }),
        catchError((errorRes) => {
          return throwError(errorRes);
        })
      );
  }

  getPicoloIpqcTableDashboardData(packId?: string) {
    let URL =
      this.EVENT_BASEURL + "/exponent/picolo/bms-ipqc/dashboard-table-data";

    if (packId) {
      URL = URL + `?pack_id=${packId}`;
    }

    return this.http.get(URL).pipe(
      map((res) => res),
      catchError((err) => throwError(err))
    );
  }

  getPiccoloIpqcTestMetaData(test_id: string) {
    return this.http
      .post(this.EVENT_BASEURL + "/exponent/picolo/bms-ipqc/test-metadata", {
        test_id,
      })
      .pipe(
        map((res) => res),
        catchError((err) => throwError(err))
      );
  }

  getPicoloMetaDataByTestId(payload) {
    return this.http
      .post(
        `${this.EVENT_BASEURL}/exponent/picolo/epack-eol/get-metadata-by-testid`,
        payload
      )
      .pipe(
        map((Response) => {
          return Response;
        }),
        catchError((errorRes) => {
          return throwError(errorRes);
        })
      );
  }

  getPiccoloPdiDashboardTableData() {
    return this.http
      .get<{
        success: boolean;
        message: string;
        data: {
          test_id: string;
          pack_id: string;
          bms_id: string;
          date: string;
          test_result: string;
        }[];
      }>(this.EVENT_BASEURL + "/exponent/picolo/pdi/dashboard-table-data", {
        observe: "response",
      })
      .pipe(
        map((res) => res),
        catchError((err) => throwError(err))
      );
  }
}
